import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { C, hexToRgba, prefersReducedMotion } from '../theme/tokens';

interface Star {
  x: number;
  y: number;
  r: number;
  baseAlpha: number;
  phase: number;
  speed: number;
}

interface ConstellationStar {
  ox: number; // offset from the constellation's own anchor point
  oy: number;
  r: number;
}

interface Constellation {
  anchorX: number;
  anchorY: number;
  stars: ConstellationStar[];
  edges: [number, number][];
  phase: number;
}

// A fixed starfield with cursor-driven parallax, not autonomous drift. An
// earlier version gave stars/constellations their own continuous velocity;
// over a normal-length visit that reads as pieces breaking off the
// WormholeGate ring and traveling away from it — wrong on two counts at
// once: the ring must stay the one fixed instrument on this page, and
// nothing should ever wander off toward an edge on its own. Real depth
// motion comes from the pointer instead: each layer (nebula, stars,
// constellations, in that far-to-near order) shifts by a small amount
// scaled to its own depth as the cursor moves, always easing back toward
// its true rest position — bounded, reversible, never a one-way trip.
const DENSITY = 34000;
const CONSTELLATION_AREA_PER = 260000; // one constellation per ~260,000px² of viewport
const MIN_CONSTELLATIONS = 3;
const MAX_CONSTELLATIONS = 8;
const CONSTELLATION_SPAN = 130; // px radius a cluster's stars spread within, before viewport scaling below

// Max parallax travel per layer, in px — deliberately small and ordered by
// apparent depth (farther layers move less, nearer layers move more).
const PARALLAX_NEBULA_PX = 8;
const PARALLAX_STAR_PX = 10;
const PARALLAX_CONSTELLATION_PX = 22;
const PARALLAX_EASE = 0.06; // per-frame lerp factor toward the pointer target — the "elastic follow" feel

/**
 * App-wide void backdrop — fixed behind everything, mounted once behind
 * `Start.tsx`'s own pre-auth states (this is the one screen the app's
 * "EVE Overview HUD" flat-chrome reset deliberately left alone, so it
 * keeps its own atmosphere budget). A slow-breathing nebula glow, two glow
 * orbs that pulse in place, a field of twinkling stars, and a handful of
 * shimmering constellations — all fixed in their true position, with a
 * bounded cursor-parallax offset layered on top. Fully static under
 * `prefers-reduced-motion` (parallax, twinkle, and shimmer all stop).
 */
const WormholeVoid: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const nebulaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const reduced = prefersReducedMotion();
    let stars: Star[] = [];
    let constellations: Constellation[] = [];
    let width = 0;
    let height = 0;
    let raf = 0;

    // Pointer target in normalized [-1, 1] space (0,0 = viewport center);
    // `current` eases toward `target` every frame rather than jumping,
    // which is what makes the parallax feel like weight/depth instead of
    // input latency.
    const pointer = { targetX: 0, targetY: 0, currentX: 0, currentY: 0 };

    const onPointerMove = (e: PointerEvent) => {
      pointer.targetX = Math.max(-1, Math.min(1, (e.clientX / window.innerWidth) * 2 - 1));
      pointer.targetY = Math.max(-1, Math.min(1, (e.clientY / window.innerHeight) * 2 - 1));
    };
    // Recenter when the pointer actually leaves the window (not just moves
    // between child elements) — `relatedTarget === null` is the reliable
    // signal for that on a document-level listener.
    const onPointerOut = (e: PointerEvent) => {
      if (e.relatedTarget === null) {
        pointer.targetX = 0;
        pointer.targetY = 0;
      }
    };

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const count = Math.max(40, Math.round((width * height) / DENSITY));
      stars = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        r: 0.4 + Math.random() * 0.9,
        baseAlpha: Math.random() * 0.4 + 0.25,
        phase: Math.random() * Math.PI * 2,
        speed: 0.15 + Math.random() * 0.35,
      }));

      const constellationCount = Math.min(
        MAX_CONSTELLATIONS,
        Math.max(MIN_CONSTELLATIONS, Math.round((width * height) / CONSTELLATION_AREA_PER)),
      );
      const span = CONSTELLATION_SPAN * Math.min(1.4, Math.max(0.7, Math.min(width, height) / 900));
      constellations = Array.from({ length: constellationCount }, () => {
        const starCount = 4 + Math.floor(Math.random() * 3); // 4-6 stars per cluster
        // A loose zigzag walk within the cluster's local space — reads as a
        // real constellation's irregular path, not a regular polygon.
        const clusterStars: ConstellationStar[] = [];
        let ox = (Math.random() - 0.5) * span * 0.4;
        let oy = (Math.random() - 0.5) * span * 0.4;
        for (let i = 0; i < starCount; i++) {
          clusterStars.push({ ox, oy, r: 1.1 + Math.random() * 1.1 });
          const angle = Math.random() * Math.PI * 2;
          const dist = span * (0.35 + Math.random() * 0.45);
          ox += Math.cos(angle) * dist;
          oy += Math.sin(angle) * dist;
        }
        // Sequential path plus one extra cross-link so it doesn't read as a
        // single straight chain — real constellations branch.
        const edges: [number, number][] = [];
        for (let i = 0; i < starCount - 1; i++) edges.push([i, i + 1]);
        if (starCount >= 4) edges.push([0, starCount - 2]);

        return {
          anchorX: Math.random() * width,
          anchorY: Math.random() * height,
          stars: clusterStars,
          edges,
          phase: Math.random() * Math.PI * 2,
        };
      });
    };

    const draw = (t: number) => {
      if (!reduced) {
        pointer.currentX += (pointer.targetX - pointer.currentX) * PARALLAX_EASE;
        pointer.currentY += (pointer.targetY - pointer.currentY) * PARALLAX_EASE;
      }

      if (nebulaRef.current) {
        nebulaRef.current.style.transform = reduced
          ? 'none'
          : `translate(${pointer.currentX * PARALLAX_NEBULA_PX}px, ${pointer.currentY * PARALLAX_NEBULA_PX}px)`;
      }

      const starOffsetX = reduced ? 0 : pointer.currentX * PARALLAX_STAR_PX;
      const starOffsetY = reduced ? 0 : pointer.currentY * PARALLAX_STAR_PX;
      const constOffsetX = reduced ? 0 : pointer.currentX * PARALLAX_CONSTELLATION_PX;
      const constOffsetY = reduced ? 0 : pointer.currentY * PARALLAX_CONSTELLATION_PX;

      ctx.clearRect(0, 0, width, height);

      stars.forEach((s) => {
        const twinkle = reduced ? 1 : 0.6 + 0.4 * Math.sin(t * 0.0007 * s.speed + s.phase);
        ctx.globalAlpha = s.baseAlpha * twinkle;
        ctx.fillStyle = C.text;
        ctx.beginPath();
        ctx.arc(s.x + starOffsetX, s.y + starOffsetY, s.r, 0, Math.PI * 2);
        ctx.fill();
      });

      constellations.forEach((c) => {
        // A very slow shared shimmer across the whole cluster's lines — one
        // constellation breathing together, not per-line noise.
        const shimmer = reduced ? 1 : 0.7 + 0.3 * Math.sin(t * 0.00018 + c.phase);
        const cx = c.anchorX + constOffsetX;
        const cy = c.anchorY + constOffsetY;

        ctx.strokeStyle = hexToRgba(C.glassEdge, 0.1 * shimmer);
        ctx.lineWidth = 1;
        ctx.beginPath();
        c.edges.forEach(([i, j]) => {
          const a = c.stars[i];
          const b = c.stars[j];
          ctx.moveTo(cx + a.ox, cy + a.oy);
          ctx.lineTo(cx + b.ox, cy + b.oy);
        });
        ctx.stroke();

        ctx.fillStyle = C.text;
        c.stars.forEach((s) => {
          ctx.globalAlpha = 0.6 * shimmer;
          ctx.beginPath();
          ctx.arc(cx + s.ox, cy + s.oy, s.r, 0, Math.PI * 2);
          ctx.fill();
        });
      });

      ctx.globalAlpha = 1;
      // Keep looping even when reduced-motion is off-parallax-only, since
      // twinkle/shimmer still animate; a fully static single frame is only
      // correct when `reduced` is true.
      if (!reduced) raf = requestAnimationFrame(draw);
    };

    resize();
    draw(performance.now());
    window.addEventListener('resize', resize);
    if (!reduced) {
      window.addEventListener('pointermove', onPointerMove);
      document.addEventListener('pointerout', onPointerOut);
    }
    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerout', onPointerOut);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <Box aria-hidden sx={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', overflow: 'hidden' }}>
      {/* Parallax translate lives on this wrapper (JS-driven, via
          `nebulaRef`); the breathing scale/opacity animation lives on the
          nested child below. Splitting them avoids one element fighting
          itself over the `transform` property between a CSS keyframe
          animation and a JS-set inline style. */}
      <Box ref={nebulaRef} sx={{ position: 'absolute', inset: '-10%' }}>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: `radial-gradient(circle at 26% 22%, ${hexToRgba(C.cyan, 0.13)} 0%, transparent 48%), radial-gradient(circle at 76% 72%, ${hexToRgba(C.glassEdge, 0.1)} 0%, transparent 44%), radial-gradient(circle at 50% 96%, ${hexToRgba(C.wormhole, 0.06)} 0%, transparent 40%)`,
            animation: prefersReducedMotion() ? 'none' : 'wormholeVoidBreathe 16s ease-in-out infinite',
            '@keyframes wormholeVoidBreathe': {
              '0%, 100%': { opacity: 0.6, transform: 'scale(1)' },
              '50%': { opacity: 1, transform: 'scale(1.06)' },
            },
          }}
        />
      </Box>
      {/* Two glow orbs — the "singularity" delight touch. Pulse (scale +
          opacity) in place only; not part of the parallax system since
          they're a brand touch, not the requested stars/constellations
          layer. */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          left: '-6%',
          bottom: '-8%',
          width: 260,
          height: 260,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${hexToRgba(C.cyan, 0.16)} 0%, transparent 70%)`,
          filter: 'blur(4px)',
          animation: prefersReducedMotion() ? 'none' : 'voidOrbPulse 22s ease-in-out infinite',
          '@keyframes voidOrbPulse': {
            '0%, 100%': { transform: 'scale(1)', opacity: 0.7 },
            '50%': { transform: 'scale(1.15)', opacity: 1 },
          },
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          right: '-4%',
          top: '-6%',
          width: 180,
          height: 180,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${hexToRgba(C.wormhole, 0.14)} 0%, transparent 70%)`,
          filter: 'blur(4px)',
          animation: prefersReducedMotion() ? 'none' : 'voidOrbPulse2 28s ease-in-out infinite',
          '@keyframes voidOrbPulse2': {
            '0%, 100%': { transform: 'scale(1)', opacity: 0.55 },
            '50%': { transform: 'scale(1.1)', opacity: 0.85 },
          },
        }}
      />
      <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
    </Box>
  );
};

export default WormholeVoid;
